/**
 * Unpaid time off, given by HR (TIME_OFF_PLAN, decision 4). Employees cannot ask for it.
 *
 * The employee is chosen from the roster, never typed, and part-timers are not offered (H4).
 * Days come in the same 2-hour blocks as PTO (H5). It is created already approved, the employee
 * is told, and the admin log records who did it with the note. It never touches the balance, and
 * everyone outside the visibility rule sees it as "Time Off".
 *
 * A face, no rules: the working comes from the API's preview, and the API enforces all of it.
 */
import { mdyRange, type Evaluation, type UnpaidCandidate } from "@scheduler/shared";
import { useEffect, useMemo, useState } from "react";

import { ApiError, useSchedulerApi, type Draft } from "../api/scheduler";
import { WorkingPanel } from "../requests/WorkingPanel";

const BLOCKS = [2, 4, 6, 8] as const;

export function UnpaidTimeOff() {
  const api = useSchedulerApi();
  const [people, setPeople] = useState<UnpaidCandidate[]>([]);
  const [personId, setPersonId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [partial, setPartial] = useState(false);
  const [perDay, setPerDay] = useState<Record<string, number>>({});
  const [note, setNote] = useState("");
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.unpaidPeople().then(setPeople, (e: unknown) => setError(e instanceof ApiError ? e.message : "Could not load the roster."));
  }, [api]);

  const workingKey = evaluation?.breakdown?.days.filter((d) => d.kind === "WORKING").map((d) => d.date).join(",") ?? "";
  const workingDays = useMemo(() => (workingKey ? workingKey.split(",") : []), [workingKey]);

  const draft: Draft | null = useMemo(() => {
    if (!personId || !start || !end) return null;
    const base: Draft = { type: "PTO", resourceIds: [personId], start, end, timeOffKind: "UNPAID" };
    return partial && workingDays.length
      ? { ...base, perDay: Object.fromEntries(workingDays.map((d) => [d, perDay[d] ?? 8])) }
      : base;
  }, [personId, start, end, partial, perDay, workingDays]);

  const draftKey = draft ? JSON.stringify(draft) : "";
  useEffect(() => {
    if (!draftKey) { setEvaluation(null); return; }
    const current = JSON.parse(draftKey) as Draft;
    let stale = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api.preview(current).then(
        (e) => { if (!stale) { setEvaluation(e); setError(null); } },
        (e: unknown) => { if (!stale) setError(e instanceof ApiError ? e.message : "Could not check this."); },
      ).finally(() => { if (!stale) setLoading(false); });
    }, 250);
    return () => { stale = true; clearTimeout(timer); };
  }, [api, draftKey]);

  async function create() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.unpaidCreate({
        resourceId: personId, start, end, note: note.trim() || null, ...(draft.perDay ? { perDay: { ...draft.perDay } } : {}),
      });
      if (!result.ok) { setEvaluation(result.evaluation); return; }
      const who = people.find((p) => p.id === personId)?.name ?? "them";
      // `told` is how many the API queued. Queued is not delivered, so "will be told" (23 Oct 2026).
      const told = result.created.told > 0 ? `${who} will be told.` : "Nobody needed telling.";
      setMessage(`Unpaid time off, ${mdyRange(start, end)} created and approved: ${result.created.chargedHours} h for ${who}. ${told}`);
      setPersonId(""); setStart(""); setEnd(""); setPartial(false); setPerDay({}); setNote(""); setEvaluation(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create it.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h3>Unpaid time off</h3>
      <p className="hint">
        Only HR gives unpaid time off. It is approved as soon as it is created, does not use the
        person's PTO balance, and shows as "Time Off" to everyone but them, their approver, HR and PMs.
      </p>
      {message ? <p className="toast" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}

      <div className="req-grid">
        <div>
          <label className="field">
            <span className="label">Employee</span>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">Choose from the roster…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <span className="hint">Part-time staff are not listed: they can't be given unpaid time off. Nor are you: another HR person gives yours.</span>
          </label>
          <div className="row2">
            <label className="field">
              <span className="label">First day</span>
              <input type="date" value={start} onChange={(e) => { setStart(e.target.value); if (!end) setEnd(e.target.value); }} />
            </label>
            <label className="field">
              <span className="label">Last day</span>
              <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          <div className="field">
            <label className="togglerow">
              <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
              <span>
                <span className="tt">Partial days</span>
                <span className="ts">2-hour blocks, the same as PTO.</span>
              </span>
            </label>
            {partial ? (
              <div className="pdbox">
                {workingDays.length ? workingDays.map((d) => (
                  <div key={d} className="pday">
                    <span className="pdname">{d}</span>
                    <div className="pdbtns" role="group" aria-label={`Hours on ${d}`}>
                      {BLOCKS.map((h) => (
                        <button key={h} type="button" className="hbtn" aria-pressed={(perDay[d] ?? 8) === h}
                          onClick={() => setPerDay({ ...perDay, [d]: h })}>{h}h</button>
                      ))}
                    </div>
                  </div>
                )) : <p className="muted">Choose the employee and dates first.</p>}
              </div>
            ) : null}
          </div>
          <label className="field">
            <span className="label">Note <span className="muted">(optional)</span></span>
            <textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
            <span className="hint">Kept in the admin log with your name. Not sent to the employee.</span>
          </label>
          <button type="button" className="primary" disabled={!evaluation?.ok || loading || busy} onClick={() => void create()}>
            {busy ? "Creating…" : "Create unpaid time off"}
          </button>
        </div>
        <WorkingPanel evaluation={evaluation} loading={loading} approverName={null} type="PTO" hrCreates />
      </div>
    </div>
  );
}
